import { cloneDeep, findLastIndex, every } from 'lodash-es';
import moment from 'moment';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { formatDateStr } from '@/pages/intelligence/utils/dateUtils';
import * as echarts from 'echarts';
import type {
  IEChartOption,
  IXAxisOption,
  IYAxisOption,
  ILegendComponentOption,
  ILineSeriesOption,
  IEChartDataEntity,
  ILineDataItemOption,
} from '@/pages/intelligence/types/echart';

type EChartSeriesOption = ILineSeriesOption[];
export interface EChartOption extends IEChartOption {
  xAxis: IXAxisOption;
  yAxis: IYAxisOption;
  series: EChartSeriesOption;
}
interface PropsType {
  options: EChartOption; // 图表options
  type: string; // 图表类型
  title: string; // 主标题
  subTitle?: string; // 副标题
  titleType: string; // 主标题类型
  sourceIcon: string; // 数据源Icon
  rank?: string | number | undefined; // 排名
  chartWidth?: string;
  locale?: string;
  isRevenue?: boolean;
}
function isInvalid(value: any) {
  if (value === 0) return false;
  return value === null || value === undefined || value === '' || isNaN(value);
}
function isValidYYYYMM(dateString: string) {
  // 尝试使用YYYY-MM格式解析日期字符串
  const parsedDate = moment(dateString, 'YYYY-MM', true); // 第三个参数为true表示严格解析

  // 检查解析是否成功，并且月份是否与输入字符串的月份相符
  if (
    parsedDate.isValid() &&
    parsedDate.format('MM') === dateString?.split('-')?.[1]
  ) {
    return true;
  }
  return false;
}
export const useFormattOption = (props: PropsType) => {
  const {
    options,
    type,
    title,
    subTitle,
    titleType,
    rank,
    sourceIcon,
    chartWidth,
    locale,
    isRevenue = false,
  } = props;
  let lineChartTitle = title;

  const optionsTmp = cloneDeep(options);
  const { color, legend, xAxis, yAxis, series } = optionsTmp;

  // 处理x轴数据
  const getXaxis = (xAxis: IXAxisOption): IXAxisOption => {
    if (Array.isArray(xAxis)) {
      xAxis = xAxis[0];
    }
    let data;
    if (
      xAxis.data &&
      Array.isArray(xAxis.data) &&
      every(xAxis.data, (item) => typeof item === 'object' && item !== null)
    ) {
      data = xAxis.data[0];
    } else {
      data = xAxis.data;
    }

    const xAxisTmp = {
      ...xAxis,
      type: 'category',
      data,
      axisLabel: {
        color: '#fff',
        opacity: 1,
        show: true,
        formatter(value: string) {
          return (
            formatDateStr(
              value,
              isValidYYYYMM(value) ? 'YYYY.MM' : 'YYYY.MM.DD',
            ) || value
          );
        },
      },
      boundaryGap: false,
    };

    if (type === 'shadow') {
      xAxisTmp.axisLine = {
        show: false, // 不显示X轴轴线
      };
      xAxisTmp.axisTick = {
        show: false, // 不显示X轴刻度线
      };
      xAxisTmp.axisLabel = {
        show: false, // 可选：不显示X轴刻度标签
      };
    }
    return xAxisTmp;
  };
  // 处理y轴数据
  const getYaxis = (yAxis: IYAxisOption): IYAxisOption => {
    if (Array.isArray(yAxis)) {
      yAxis = yAxis[0];
    }
    let data;
    if (
      yAxis.data &&
      Array.isArray(yAxis.data) &&
      every(yAxis.data, (item) => typeof item === 'object' && item !== null)
    ) {
      data = yAxis.data[0];
    } else {
      data = yAxis.data;
    }

    const yAxisTmp = {
      ...yAxis,
      data,
      type: 'value',
      axisLabel: {
        color: '#fff',
        opacity: 1,
        show: true,
        formatter(value: string) {
          return newMathFormat(value) || value;
        },
      },
    };

    if (type === 'shadow') {
      // 不显示X轴轴线和刻度标签
      yAxisTmp.splitLine = {
        show: false, //
      };
      yAxisTmp.axisLabel = {
        show: false,
      };
      yAxisTmp.max = function (value: { max: number; }) {
        return value.max + value.max * 0.5; // 手动加长纵坐标，否则峰值和总值的markPoint的布局会挤到上边界
      };
    }
    return yAxisTmp;
  };
  // 处理图例数据
  const getLegend = (legend: ILegendComponentOption) => {
    if (type === 'shadow') {
      return {
        show: false, // 隐藏图例
      };
    }
    const textStyle = legend?.textStyle || {};
    return {
      ...legend,
      type: 'plain',
      bottom: 2,
      itemGap: Number(chartWidth) < 1070 ? 2 : 6,
      textStyle: {
        ...textStyle,
        color: '#fff',
      },
    };
  };

  // 处理series数据 由于历史遗留，图表的option格式非常不统一，series参数需处理为统一格式，方便后续操作EChartSeriesOption
  const getSeriesData = (series: EChartSeriesOption): EChartSeriesOption =>
    // 这里把主要把Ssries.data的格式统一为 [{name:string,value:string....},{name:string,value:string....}]
    series?.map((item: IEChartDataEntity) => {
      const itemTmp = item;
      const seriesData = item?.data?.map((d: IEChartDataEntity, i: number) => {
        // 暂存当前数据前一项和后一项的值，有的数据会出现断层，某个时间只有一个数据且前后都没有数据的情况下，要高亮该点
        const preValue = item?.data?.[i - 1]?.value;
        const behValue = item?.data?.[i + 1]?.value;
        if (Array.isArray(d?.value) && d?.value?.length) {
          // 这里的判断是因为历史遗留的数据格式，data.value是一个数组... --> [ '2024-01-01' , '200']
          return {
            name: d?.name,
            value: d?.value[1],
            itemStyle: {
              opacity:
                isInvalid(preValue?.[1]) && isInvalid(behValue?.[1]) ? 1 : 0,
            },
          };
        }
        return {
          name: d?.name,
          value: d?.value,
          itemStyle: {
            opacity: isInvalid(preValue) && isInvalid(behValue) ? 1 : 0,
          },
        };
      });

      // 由于外部传入的只是折线图，切换shadow类型时，需要手动增加阴影
      if (!item.areaStyle) {
        itemTmp.areaStyle = {
          color: new echarts.graphic.LinearGradient(
            0,
            0,
            0,
            1, // 渐变方向
            [
              {
                offset: 0,
                color: 'rgba(54,100,213,1)',
              },
              {
                offset: 1,
                color: 'rgba(54,100,213, 0)',
              },
            ],
          ),
          opacity: 0.7,
          shadowColor: 'rgba(0, 0, 0, 0.2)', // 区域阴影颜色
          shadowOffsetX: 5, // 阴影在X轴上的偏移量
          shadowOffsetY: 5, // 阴影在Y轴上的偏移量
          shadowBlur: 5, // 阴影的模糊大小
        };
      }
      itemTmp.emphasis.itemStyle.borderColor = '#fff';
      itemTmp.emphasis.itemStyle.borderWidth = 2;

      return {
        ...itemTmp,
        lineStyle: item?.lineStyle || {},
        itemStyle: item?.itemStyle || {},
        data: seriesData,
        stack: type === 'shadow' ? 'total' : undefined,
        symbolSize: 6,
      };
    });
  // 获取总值
  const getSumValue = (series: EChartSeriesOption) => {
    let sum = 0;
    series?.forEach((item: IEChartDataEntity) => {
      item?.data?.forEach((i: IEChartDataEntity) => {
        sum += Number(i?.value) || 0;
      });
    });
    return newMathFormat(sum, true);
  };
  // 获取排名
  const getRank = (rank: string | number | undefined) => {
    if (!rank || rank === '-') return '';
    return `${locale === 'zh-CN' ? '排名' : 'Rank'} #${rank}`;
  };
  const getBottom = (legend: ILegendComponentOption, type: string) => {
    if (type === 'shadow') return 24;
    if (!legend?.show) return '6%';
    if (Number(chartWidth) < 1070) {
      return '16%';
    }
    return '10%';
  };

  const getShowStartLine = (maxindex: number, lineOption: EChartOption) => {
    if (maxindex === 0) return false;
    if (chartWidth === '480') {
      return (maxindex + 1) / lineOption?.xAxis?.data?.length > 0.2;
    }
    return (maxindex + 1) / lineOption?.xAxis?.data?.length > 0.35;
  };
  const getShowEndLine = (maxindex: number, lineOption: EChartOption) => {
    if (maxindex === lineOption?.xAxis?.data?.length - 1) return false;
    if (chartWidth === '480') {
      return (maxindex + 1) / lineOption?.xAxis?.data?.length < 0.8;
    }
    return (maxindex + 1) / lineOption?.xAxis?.data?.length < 0.7;
  };
  const lineOption: EChartOption = {
    backgroundColor: '#3B3D4B',
    title: {},
    color,
    grid: {
      left: type === 'shadow' ? 34 : 14,
      right: type === 'shadow' ? 34 : Number(chartWidth) < 1070 ? 28 : 38,
      top: type === 'shadow' ? '40%' : 40,
      bottom: getBottom(legend as ILegendComponentOption, type),
      containLabel: true,
    },
    legend: getLegend(legend as ILegendComponentOption),
    xAxis: getXaxis(xAxis),
    yAxis: getYaxis(yAxis),
    series: getSeriesData(series as EChartSeriesOption),
  };

  if (lineOption?.series && type === 'shadow') {
    // 数据掐头去尾，如果前后某些时间段没数据，去掉。
    // 多个legend的情况下 有数据的最小日期前面的都去掉，有数据的最大日期后面的都去掉
    const preTmp = lineOption?.series?.map((item: IEChartDataEntity) =>
      item?.data?.findIndex((i: IEChartDataEntity) => !isInvalid(i.value)),
    );
    const postTmp = lineOption?.series?.map((item: IEChartDataEntity) =>
      findLastIndex(item?.data, (i: IEChartDataEntity) => !isInvalid(i.value)),
    );
    const preTmpFilter = preTmp?.filter((item: number) => item >= 0);
    const minValidIndex = Math.min(...preTmpFilter);
    const maxValidIndex = Math.max(...postTmp);

    if (minValidIndex >= 0 && maxValidIndex >= 0) {
      lineOption.xAxis.data = lineOption?.xAxis?.data?.slice(
        minValidIndex,
        maxValidIndex + 1,
      );
      lineOption?.series?.forEach((seriesItem: IEChartDataEntity) => {
        seriesItem.data = seriesItem?.data?.slice(
          minValidIndex,
          maxValidIndex + 1,
        );
      });
    }
  }
  // 用于存储每天的总和和最大值的对象
  let maxSum = 0;
  let maxIndex = 0;
  let startSum = 0;
  let endSum = 0;
  let maxDate = '';
  let xEndIndex = lineOption?.xAxis?.data?.length - 1;
  const totalData: number[] = [];
  if (type === 'shadow') {
    /**
     * PC Game 详情下载 shadow chart，PCU、ACU 指标逻辑
     * 有 PCU 只取 PCU
     * 无 PCU 有 ACU 只取 ACU
     */
    const pcuSeries = lineOption.series?.find((item) => item.name === 'PCU');
    const acuSeries = lineOption.series?.find((item) => item.name === 'ACU');
    if (pcuSeries) {
      // 指定 markLine x轴 endIndex
      const lastValidIndex = pcuSeries.data?.findLastIndex(
        (item) => !isInvalid((item as { value?: number; })?.value),
      );
      xEndIndex = lastValidIndex || xEndIndex;
      // 替换 series 数据为 pcuSeries
      lineOption.series = [pcuSeries];
    } else if (acuSeries) {
      // 指定 markLine x轴 endIndex
      const lastValidIndex = acuSeries.data?.findLastIndex(
        (item) => !isInvalid((item as { value?: number; })?.value),
      );
      xEndIndex = lastValidIndex || xEndIndex;
      // 替换 series 数据为 acuSeries
      lineOption.series = [acuSeries];
      // 展示 ACU 时标题显示 ACU
      lineChartTitle = lineChartTitle.replace('PCU', 'ACU');
    }

    lineOption?.xAxis?.data.forEach((date: string, index: number) => {
      let sum: null | number = null;

      // 遍历每个series的数据，找到对应日期的值并相加
      lineOption?.series?.forEach((series: IEChartDataEntity) => {
        if (
          series?.data[index] !== undefined &&
          series?.data[index] !== null &&
          !isInvalid(series?.data?.[index]?.value)
        ) {
          if (sum === null) {
            sum = 0;
          }
          sum += series.data[index].value;
          if (index === 0) {
            startSum += series?.data?.[index]?.value;
          }
          if (index === xEndIndex) {
            endSum += series?.data?.[index]?.value;
          }
        }
      });
      totalData.push(sum);

      // 更新每日总和和最大值
      if (sum && sum > maxSum) {
        maxSum = sum;
        maxDate = date;
        maxIndex = index;
      }
    });
    lineOption.series.splice(1);
    lineOption.series[0].data = lineOption?.series?.[0]?.data?.map(
      (sItem: ILineDataItemOption, sIndex: number) => {
        const preValue = totalData?.[sIndex - 1];
        const behValue = totalData?.[sIndex + 1];
        const itemStyle = sItem.itemStyle || {};
        return {
          ...sItem,
          value: totalData[sIndex],
          itemStyle: {
            ...itemStyle,
            opacity: isInvalid(preValue) && isInvalid(behValue) ? 1 : 0,
          },
        };
      },
    );
    const startDate = lineOption?.xAxis?.data[0];
    const endDate = lineOption?.xAxis?.data[xEndIndex];
    const MAX = newMathFormat(maxSum, true);
    const STARTSUM = newMathFormat(startSum, true);
    const ENDSUM = newMathFormat(endSum, true);
    const STARTDATE = formatDateStr(
      startDate,
      isValidYYYYMM(startDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
    );
    const ENDTDATE = formatDateStr(
      endDate,
      isValidYYYYMM(endDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
    );
    const MAXDATE = formatDateStr(
      maxDate,
      isValidYYYYMM(maxDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
    );
    const timeQuantum = `${STARTDATE} - ${ENDTDATE}`;
    const isShowStartLine = getShowStartLine(maxIndex, lineOption);
    const isShowEndLine = getShowEndLine(maxIndex, lineOption);
    lineOption.series[0].markLine = {
      // markLine 的配置
      symbol: ['none', 'none'],
      label: { show: false },
      data: [
        [
          {
            name: 'start',
            coord: [maxIndex, 0],
            label: {
              formatter: () =>
                formatDateStr(
                  maxDate,
                  isValidYYYYMM(maxDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
                ),
              position: 'start',
              color: '#fff',
              show: true,
              padding: [2, 0, 0, 0],
              fontSize: 10,
            },
          },
          { coord: [maxIndex, maxSum] },
        ],
        [
          { coord: [maxIndex, maxSum] },
          {
            coord: [maxIndex, maxSum + maxSum * 0.3],
            lineStyle: {
              color: '#d2d4d8', // 线条颜色
              type: 'solid', // 线条类型，可选'solid'、'dashed'等
            },
            label: {
              formatter: () => MAX?.toString(),
              position: 'end',
              color: '#fff',
              fontSize: 10,
              show: !!maxSum,
              padding: [0, 0, -4, 0],
            },
          },
        ],
      ],
      lineStyle: {
        color: '#d2d4d8',
        type: 'dashed',
      },
    };
    if (!startSum && !maxSum) {
      lineOption?.series?.[0]?.markLine?.data?.push(
        [
          {
            coord: [0, 0],
            label: {
              formatter: () => STARTDATE,
              position: 'start',
              color: '#fff',
              padding: [2, 0, 0, 0],
              show: true,
              fontSize: 10,
            },
          },
          {
            coord: [0, '0.3'],
          },
        ],
        [
          {
            coord: [0, 0],
            label: {
              formatter: () => '0',
              position: 'end',
              color: '#fff',
              show: true,
              fontSize: 10,
            },
          },
          {
            coord: [0, '0.3'],
          },
        ],
      );
    } else {
      lineOption?.series?.[0]?.markLine?.data?.push(
        [
          {
            coord: [0, 0],
            label: {
              formatter: () => STARTDATE,
              position: 'start',
              color: '#fff',
              show: !!startSum && isShowStartLine,
              padding: [2, 0, 0, 0],
              fontSize: 10,
            },
            lineStyle: {
              opacity: !!startSum && isShowStartLine ? 1 : 0,
            },
          },
          {
            coord: [0, startSum],
            lineStyle: {
              opacity: !!startSum && isShowStartLine ? 1 : 0,
            },
          },
        ],
        [
          { coord: [0, startSum] },
          {
            coord: [0, maxSum],
            lineStyle: {
              type: 'solid',
              opacity: !!startSum && isShowStartLine ? 1 : 0,
            },
            label: {
              formatter: () => STARTSUM?.toString(),
              position: 'end',
              color: '#fff',
              show: !!startSum && isShowStartLine,
              fontSize: 10,
            },
          },
        ],
      );
    }

    if (!endSum && !maxSum) {
      lineOption?.series?.[0]?.markLine?.data?.push(
        [
          {
            coord: [xEndIndex, 0],
            label: {
              formatter: () => ENDTDATE,
              position: 'start',
              color: '#fff',
              show: true,
              padding: [2, 0, 0, 0],
              fontSize: 10,
            },
          },
          {
            coord: [xEndIndex, '0.3'],
          },
        ],
        [
          {
            coord: [xEndIndex, 0],
            label: {
              formatter: () => '0',
              position: 'end',
              color: '#fff',
              show: true,
              fontSize: 10,
            },
          },
          {
            coord: [xEndIndex, '0.3'],
          },
        ],
      );
    } else {
      lineOption?.series?.[0]?.markLine?.data?.push(
        [
          {
            coord: [xEndIndex, 0],
            label: {
              formatter: () => ENDTDATE,
              position: 'start',
              color: '#fff',
              padding: [2, 0, 0, 0],
              show: !!endSum && isShowEndLine,
              fontSize: 10,
            },
            lineStyle: {
              opacity: !!endSum && isShowEndLine ? 1 : 0,
            },
          },
          { coord: [xEndIndex, endSum] },
        ],
        [
          { coord: [xEndIndex, endSum] },
          {
            coord: [xEndIndex, maxSum],
            lineStyle: {
              type: 'solid',
              opacity: !!endSum && isShowEndLine ? 1 : 0,
            },
            label: {
              formatter: () => ENDSUM?.toString(),
              position: 'end',
              color: '#fff',
              show: !!endSum && isShowEndLine,
              fontSize: 10,
            },
          },
        ],
      );
    }
    // 处理标题
    lineOption.title = {
      text: [`{icon|}`, `{title|${lineChartTitle}}`].join(''),
      textStyle: {
        rich: {
          // 定义富文本样式
          icon: {
            height: sourceIcon ? 24 : 0,
            width: sourceIcon ? 24 : 0,
            backgroundColor: {
              image: sourceIcon,
            },
          },
          title: {
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'PingFang SC',
            padding: [0, 0, 0, sourceIcon ? 4 : 0],
          },
        },
      },
      subtext: [
        `{a|${subTitle} ${titleType === 'Peak' ? MAXDATE : timeQuantum}}`,
        `{b|${isRevenue ? '$' : ''}${
          titleType === 'Peak' ? MAX : getSumValue(lineOption.series)
        }}  {c|${getRank(rank)}}`,
      ].join('\n'),
      subtextStyle: {
        rich: {
          // 定义富文本样式
          a: {
            // 样式a
            color: '#fff',
            fontSize: 12,
            fontFamily: 'PingFang SC',
            padding: [-4, 6, 6, 0],
          },
          b: {
            // 样式b
            color: '#fff',
            fontWeight: 600,
            fontSize: 28,
            fontFamily: 'Roboto,PingFang SC',
            padding: [0, 42, 0, 0],
          },
          c: {
            // 样式a
            color: '#fff',
            fontSize: 12,
            fontFamily: 'PingFang SC',
          },
        },
      },
    };

    /* series只有一个点的时候需要特殊处理 start*/
    lineOption?.series?.forEach((seriesItem: IEChartDataEntity) => {
      if (
        seriesItem?.data?.length === 1 &&
        lineOption?.xAxis?.data?.length === 1
      ) {
        lineOption.xAxis.data = [
          lineOption.xAxis.data[0],
          lineOption.xAxis.data[0],
        ];
        seriesItem.data = [seriesItem.data[0], seriesItem.data[0]];
        seriesItem.markLine = {
          symbol: ['none', 'none'],
          label: { show: false },
          data: [
            [
              {
                coord: [0, 0],
                label: {
                  formatter: () =>
                    formatDateStr(
                      startDate,
                      isValidYYYYMM(startDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
                    ),
                  position: 'start',
                  color: '#fff',
                  show: !!startSum,
                },
              },
              {
                coord: [0, startSum],
              },
            ],
            [
              { coord: [0, startSum] },
              {
                coord: [0, startSum + startSum * 0.2],
                lineStyle: {
                  type: 'solid',
                },
                label: {
                  formatter: () => STARTSUM,
                  position: 'end',
                  color: '#fff',
                  show: !!startSum,
                },
              },
            ],
            [
              {
                coord: [1, 0],
                label: {
                  formatter: () =>
                    formatDateStr(
                      endDate,
                      isValidYYYYMM(endDate) ? 'YYYY.MM' : 'YYYY.MM.DD',
                    ),
                  position: 'start',
                  color: '#fff',
                  show: !!endSum,
                },
              },
              { coord: [1, endSum] },
            ],
            [
              { coord: [1, endSum] },
              {
                coord: [1, endSum + endSum * 0.2],
                lineStyle: {
                  type: 'solid',
                },
                label: {
                  formatter: () => ENDSUM,
                  position: 'end',
                  color: '#fff',
                  show: !!endSum,
                },
              },
            ],
          ],
          lineStyle: {
            color: '#d2d4d8',
            type: 'dashed',
          },
        };
      }
    });
    /* series只有一个点的时候需要特殊处理 end*/

    if (lineOption.series && Array.isArray(lineOption?.series)) {
      for (const seriesItem of lineOption.series) {
        if (seriesItem?.type === 'line') {
          seriesItem.lineStyle!.color = 'rgb(54,100,213)';
          seriesItem.itemStyle!.color = 'rgb(54,100,213)';
          seriesItem.areaStyle = {
            color: new echarts.graphic.LinearGradient(
              0,
              0,
              0,
              1, // 渐变方向
              [
                {
                  offset: 0,
                  color: 'rgba(54,100,213,1)',
                },
                {
                  offset: 0.5,
                  color: 'rgba(54,100,213,0.8)',
                },
                {
                  offset: 1,
                  color: 'rgba(54,100,213, 0)',
                },
              ],
            ),
            shadowColor: 'rgba(0, 0, 0, 0.2)', // 区域阴影颜色
            shadowOffsetX: 5, // 阴影在X轴上的偏移量
            shadowOffsetY: 5, // 阴影在Y轴上的偏移量
            shadowBlur: 5, // 阴影的模糊大小
          };
        }
      }
    }
  }

  if (type === 'line') {
    lineOption.title = {
      text: lineChartTitle,
      textStyle: {
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'PingFang SC',
        color: '#fff',
      },
      top: 6,
      left: 2,
    };
    lineOption.xAxis.axisLabel = {
      color: '#fff',
      opacity: 1,
      show: true,
      padding: [2, 0, 0, 0],
      formatter(value: string) {
        return (
          formatDateStr(
            value,
            isValidYYYYMM(value) ? 'YYYY.MM' : 'YYYY.MM.DD',
          ) || value
        );
      },
    };

    lineOption.legend.textStyle.color = '#fff';
    if (lineOption.legend.textStyle?.rich?.a) {
      lineOption.legend.textStyle.rich.a.color = '#fff';
    }
    lineOption.yAxis.splitLine.lineStyle.color = '#fff';
    for (const item of lineOption.series) {
      item.areaStyle = {
        opacity: 0,
      };
      item.markLine = {};
    }
  }

  return {
    resOptions: lineOption,
    maxIndex,
    isShowStartLine: getShowStartLine(maxIndex, lineOption),
    isShowEndLine: getShowEndLine(maxIndex, lineOption),
  };
};
